import React, { ReactNode, useState } from 'react'
import styled, { css } from 'styled-components'
import logoFull from '../../assets/logo-full-dark.png'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useSettings } from '../../contexts/SettingsContext'
import DashboardIcon from '@mui/icons-material/Dashboard'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import InventoryIcon from '@mui/icons-material/Inventory'
import RouterIcon from '@mui/icons-material/Router'
import GroupsIcon from '@mui/icons-material/Groups'
import BarChartIcon from '@mui/icons-material/BarChart'
import AssignmentIcon from '@mui/icons-material/Assignment'
import PersonIcon from '@mui/icons-material/Person'
import NotificationsIcon from '@mui/icons-material/Notifications'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import BugReportIcon from '@mui/icons-material/BugReport'
import SettingsIcon from '@mui/icons-material/Settings'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import AgricultureIcon from '@mui/icons-material/Agriculture'
import NotificationBell from './NotificationBell'
import ChatWidget from './ChatWidget'

/**
 * ELAP is hidden from the sidebar for now (all roles). The /elap route and
 * ElapScreen still work when opened directly — flip to true to restore the link.
 */
const SHOW_ELAP_IN_MENU = false

/**
 * Main layout container
 */
const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`

/**
 * Backdrop overlay for mobile menu
 */
const MenuBackdrop = styled.div<{ $isOpen?: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`

/**
 * Side menu container
 */
const SideMenu = styled.aside<{ $isRTL?: boolean; $isOpen?: boolean }>`
  width: 300px;
  background: linear-gradient(180deg, #2a2a31 0%, #1b1b21 100%);
  color: ${props => props.theme.colors.white};
  display: flex;
  flex-direction: column;
  box-shadow: ${props => props.theme.shadows.xl};
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1001;
  transition: transform 0.3s ease;
  ${props => props.$isRTL ? css`right: 0;` : css`left: 0;`}

  @media (max-width: 768px) {
    width: 280px;
    ${props => props.$isRTL 
      ? css`transform: translateX(${props.$isOpen ? '0' : '280px'});`
      : css`transform: translateX(${props.$isOpen ? '0' : '-280px'});`
    }
  }

  @media (max-width: 480px) {
    width: 250px;
    ${props => props.$isRTL 
      ? css`transform: translateX(${props.$isOpen ? '0' : '250px'});`
      : css`transform: translateX(${props.$isOpen ? '0' : '-250px'});`
    }
  }
`

/**
 * Logo section
 */
const LogoSection = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`

/**
 * Logo text
 */
const Logo = styled.h1`
  font-size: ${props => props.theme.typography.fontSize.xlarge};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.white};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  letter-spacing: -0.5px;
`

/**
 * Full brand logo (mark + wordmark + tagline) shown at the top of the sidebar
 */
const LogoImage = styled.img`
  width: 100%;
  max-width: 200px;
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0 auto;
`

/**
 * Menu list
 */
const MenuList = styled.nav`
  flex: 1;
  padding: ${props => props.theme.spacing.md} 0;
`

/**
 * Menu item
 */
const MenuItem = styled(Link)<{ active?: boolean; $isRTL?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  min-height: 40px;
  color: ${props => props.theme.colors.white};
  text-decoration: none;
  transition: all ${props => props.theme.transitions.normal};
  background: ${props =>
    props.active ? 'rgba(246, 180, 0, 0.16)' : 'transparent'};
  ${props => props.$isRTL
    ? css`border-right: 4px solid ${props.active ? '#f6b400' : 'transparent'};`
    : css`border-left: 4px solid ${props.active ? '#f6b400' : 'transparent'};`
  }
  position: relative;
  font-weight: ${props =>
    props.active
      ? props.theme.typography.fontWeight.semibold
      : props.theme.typography.fontWeight.medium};
  font-size: ${props => props.theme.typography.fontSize.medium};
  opacity: ${props => (props.active ? 1 : 0.85)};
  margin: ${props => props.theme.spacing.xs};
  gap: ${props => props.theme.spacing.xs};
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    opacity: 1;
  }
`

/**
 * Menu item icon
 */
const MenuItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 18px;

  svg {
    font-size: 18px;
  }
`

/**
 * Menu item text
 */
const MenuItemText = styled.span`
  font-size: ${props => props.theme.typography.fontSize.medium};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: 0.2px;
`

/**
 * User section
 */
const UserSection = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.md};
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`

/**
 * User info
 */
const UserInfo = styled.div`
  margin-bottom: ${props => props.theme.spacing.sm};
`

/**
 * User name
 */
const UserName = styled.div`
  font-size: ${props => props.theme.typography.fontSize.medium};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.white};
  margin-bottom: ${props => props.theme.spacing.xs};
  letter-spacing: 0.3px;
`

/**
 * User role
 */
const UserRole = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: rgba(255, 255, 255, 0.7);
  text-transform: capitalize;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`

/**
 * Logout button
 */
const LogoutButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.2);
  color: ${props => props.theme.colors.white};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.theme.borderRadius.large};
  font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.normal};
  letter-spacing: 0.3px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`

/**
 * Main content area
 */
const MainContent = styled.main<{ $isRTL?: boolean }>`
  flex: 1;
  ${props => props.$isRTL ? css`margin-right: 300px;` : css`margin-left: 300px;`}
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.lg};
  min-height: 100vh;
  background-color: transparent;

  @media (max-width: 768px) {
    ${props => props.$isRTL ? css`margin-right: 0;` : css`margin-left: 0;`}
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.md};
  }

  @media (max-width: 480px) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm};
  }
`

/**
 * App bar
 */
const AppBar = styled.div<{ $isRTL?: boolean }>`
  position: fixed;
  top: 0;
  ${props => props.$isRTL ? css`right: 300px; left: 0;` : css`left: 300px; right: 0;`}
  height: 64px;
  background: ${props => props.theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${props => props.theme.spacing.lg};
  gap: ${props => props.theme.spacing.md};
  z-index: 999;
  box-shadow: ${props => props.theme.shadows.sm};

  @media (max-width: 768px) {
    ${props => props.$isRTL ? css`right: 0; left: 0;` : css`left: 0; right: 0;`}
    padding: 0 ${props => props.theme.spacing.md};
  }
`

/**
 * Menu toggle button
 */
const MenuToggleButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  font-size: 24px;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  margin: 0;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  svg {
    font-size: 24px;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

/**
 * App bar content wrapper
 */
const AppBarContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`


/**
 * App bar user info
 */
const AppBarUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.medium};
  background: ${props => props.theme.colors.background};
`

/**
 * App bar user details
 */
const AppBarUserDetails = styled.div`
  display: flex;
  flex-direction: column;
`

/**
 * App bar user name
 */
const AppBarUserName = styled.span`
  font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
`

/**
 * App bar user role
 */
const AppBarUserRole = styled.span`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: capitalize;
  line-height: 1.2;
`


/**
 * Content wrapper with padding for app bar
 */
const ContentWrapper = styled.div`
  padding-top: 80px;
`

/**
 * Breadcrumbs container
 */
const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
`

/**
 * Breadcrumb item
 */
const BreadcrumbItem = styled.span<{ active?: boolean }>`
  color: ${props =>
    props.active ? props.theme.colors.text : props.theme.colors.textSecondary};
  font-weight: ${props =>
    props.active
      ? props.theme.typography.fontWeight.semibold
      : props.theme.typography.fontWeight.medium};
`

/**
 * Breadcrumb separator
 */
const BreadcrumbSeparator = styled.span`
  color: ${props => props.theme.colors.textSecondary};
`

/**
 * Layout component with side menu
 * @param props - Component props
 * @returns Layout component
 */
function Layout({ children }: { children: ReactNode }): React.JSX.Element {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { isRTL } = useSettings()
  const [menuOpen, setMenuOpen] = useState(false)

  // Get breadcrumbs for current route
  const getBreadcrumbItems = (): string[] => {
    const path = location.pathname
    if (path === '/' || path === '/dashboard') return [t('navigation.dashboard')]
    if (path === '/yards') return [t('navigation.yards')]
    if (path.startsWith('/yards/')) return [t('navigation.yards'), t('yard.detail')]
    if (path === '/inventory') return [t('navigation.inventory')]
    if (path === '/teams') return [t('navigation.teams')]
    if (path === '/analysis') return [t('navigation.analysis')]
    if (path === '/tasks') return [t('navigation.tasks')]
    if (path === '/devices') return [t('navigation.devices')]
    if (path.startsWith('/devices/')) return [t('navigation.devices'), t('navigation.deviceDetails')]
    if (path === '/recommendations') return [t('navigation.recommendations')]
    if (path === '/reports') return [t('navigation.reports')]
    if (path === '/settings') return [t('settings.title')]
    if (path === '/users') return [t('users.title')]
    if (path === '/user-management') return [t('users.title')]
    if (path.startsWith('/user-management/')) return [t('users.title'), t('navigation.userDetails')]
    if (path === '/accounts') return [t('navigation.accounts')]
    if (path === '/elap') return [t('navigation.elap')]
    if (path === '/debug') return [t('navigation.debug')]
    return []
  }

  /**
   * Localized label for a user role (falls back to the raw role name)
   */
  function roleLabel(role: string): string {
    return t(`users.roles.${role}`, { defaultValue: role.replace('_', ' ') })
  }

  /**
   * Handles logout
   * @returns Promise void
   */
  async function handleLogout(): Promise<void> {
    await logout()
    navigate('/login')
  }

  /**
   * Checks if route is active
   * @param path - Route path
   * @returns boolean
   */
  function isActive(path: string): boolean {
    return location.pathname === path
  }

  return (
    <LayoutContainer>
      <MenuBackdrop $isOpen={menuOpen} onClick={() => setMenuOpen(false)} />
      <SideMenu $isRTL={isRTL} $isOpen={menuOpen}>
        <LogoSection>
          <Logo>
            <LogoImage src={logoFull} alt="Hivenetic" />
          </Logo>
        </LogoSection>
        <MenuList>
          <MenuItem to="/dashboard" active={isActive('/dashboard')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <DashboardIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.dashboard')}</MenuItemText>
          </MenuItem>
          <MenuItem to="/yards" active={isActive('/yards')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <LocationOnIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.yards')}</MenuItemText>
          </MenuItem>
          <MenuItem to="/inventory" active={isActive('/inventory')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <InventoryIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.inventory')}</MenuItemText>
          </MenuItem>
          <MenuItem to="/devices" active={isActive('/devices')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <RouterIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.devices')}</MenuItemText>
          </MenuItem>
          <MenuItem to="/teams" active={isActive('/teams')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <GroupsIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.teams')}</MenuItemText>
          </MenuItem>
          <MenuItem to="/analysis" active={isActive('/analysis')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <BarChartIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.analysis')}</MenuItemText>
          </MenuItem>
          <MenuItem to="/tasks" active={isActive('/tasks')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <AssignmentIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.tasks')}</MenuItemText>
          </MenuItem>
          <MenuItem to="/recommendations" active={isActive('/recommendations')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <NotificationsIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.recommendations')}</MenuItemText>
          </MenuItem>
          <MenuItem to="/reports" active={isActive('/reports')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <AssignmentIcon />
            </MenuItemIcon>
            <MenuItemText>{t('navigation.reports')}</MenuItemText>
          </MenuItem>
          {SHOW_ELAP_IN_MENU && (
            <MenuItem to="/elap" active={isActive('/elap')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
              <MenuItemIcon>
                <AgricultureIcon />
              </MenuItemIcon>
              <MenuItemText>{t('navigation.elap')}</MenuItemText>
            </MenuItem>
          )}
          {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'owner') && (
            <MenuItem to="/users" active={isActive('/users')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
              <MenuItemIcon>
                <PersonIcon />
              </MenuItemIcon>
              <MenuItemText>{t('users.title')}</MenuItemText>
            </MenuItem>
          )}
          {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'owner') && (
            <MenuItem to="/accounts" active={isActive('/accounts')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
              <MenuItemIcon>
                <AccountTreeIcon />
              </MenuItemIcon>
              <MenuItemText>{t('navigation.accounts')}</MenuItemText>
            </MenuItem>
          )}
          {user?.role === 'super_admin' && (
            <MenuItem to="/debug" active={isActive('/debug')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
              <MenuItemIcon>
                <BugReportIcon />
              </MenuItemIcon>
              <MenuItemText>{t('navigation.debug')}</MenuItemText>
            </MenuItem>
          )}
          <MenuItem to="/settings" active={isActive('/settings')} $isRTL={isRTL} onClick={() => setMenuOpen(false)}>
            <MenuItemIcon>
              <SettingsIcon />
            </MenuItemIcon>
            <MenuItemText>{t('settings.title')}</MenuItemText>
          </MenuItem>
        </MenuList>
        <UserSection>
          {user && (
            <>
              <UserInfo>
                <UserName>{user.name}</UserName>
                <UserRole>{roleLabel(user.role)}</UserRole>
              </UserInfo>
              <LogoutButton onClick={handleLogout}>
                {t('auth.signOut')}
              </LogoutButton>
            </>
          )}
        </UserSection>
      </SideMenu>
      <MainContent $isRTL={isRTL}>
        <AppBar $isRTL={isRTL}>
          <MenuToggleButton onClick={() => setMenuOpen(!menuOpen)} aria-label={t('navigation.toggleMenu')}>
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </MenuToggleButton>
          <Breadcrumbs>
            <BreadcrumbItem>{t('navigation.brand')}</BreadcrumbItem>
            {getBreadcrumbItems().map((item, index, items) => (
              <React.Fragment key={`${item}-${index}`}>
                <BreadcrumbSeparator>/</BreadcrumbSeparator>
                <BreadcrumbItem active={index === items.length - 1}>{item}</BreadcrumbItem>
              </React.Fragment>
            ))}
          </Breadcrumbs>
          <AppBarContent>
            <NotificationBell />
            {user && (
              <AppBarUserInfo>
                <AppBarUserDetails>
                  <AppBarUserName>{user.name}</AppBarUserName>
                  <AppBarUserRole>{roleLabel(user.role)}</AppBarUserRole>
                </AppBarUserDetails>
              </AppBarUserInfo>
            )}
          </AppBarContent>
        </AppBar>
        <ContentWrapper>{children}</ContentWrapper>
      </MainContent>
      <ChatWidget />
    </LayoutContainer>
  )
}

export default Layout
